import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';

const UserDropdown = () => {
  const { user, isAuthenticated, logout } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    navigate('/');
  };

 // If not logged in, show login/register link
if (!isAuthenticated) {
  return (
    <div className="d-flex align-items-center" style={{ marginLeft: '10px' }}>
      <Link 
        to="/account" 
        className="d-flex align-items-center gap-2 text-dark text-sm py-2 px-3 rounded-pill hover-bg-light transition-1"
        style={{ textDecoration: 'none', color: '#333' }}
      >
        <span className="icon d-flex" style={{ fontSize: '20px' }}>
          <i className="ph ph-user-circle" />
        </span>
        {/* 👇 Hide text on mobile, show on desktop */}
        <span className="d-none d-lg-inline" style={{ fontWeight: '500' }}>Login / Register</span>
      </Link>
    </div>
  );
}

  // Get initials for avatar
  const getInitials = () => {
    if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="position-relative" ref={dropdownRef} style={{ zIndex: 9999 }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-dark text-sm py-8 flex-align gap-6 bg-transparent border-0 cursor-pointer"
        style={{ background: 'none', border: 'none' }}
      >
        <span className="icon text-md d-flex position-relative">
          <div className="w-32 h-32 rounded-circle bg-main-800 text-white flex-center fw-bold">
            {getInitials()}
          </div>
          <span className="w-10 h-10 rounded-circle bg-success border border-white position-absolute bottom-0 end-0" />
        </span>
        {/* 👇 Hide username on mobile, show on desktop */}
        <span className="hover-text-decoration-underline d-none d-lg-inline">
          {user?.username?.split(' ')[0] || 'My Account'}
        </span>
        {/* 👇 Hide arrow on mobile, show on desktop */}
        <span className={`arrow-icon text-sm d-none d-lg-flex transition-3 ${isOpen ? 'rotate-180' : ''}`}>
          <i className="ph ph-caret-down" />
        </span>
      </button>

      {/* Dropdown Menu - FIXED ALIGNMENT */}
{isOpen && (
  <div 
    className="position-absolute top-100 mt-2 bg-white rounded-8 shadow-lg py-8"
    style={{ 
      width: '280px',
      zIndex: 9999,
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
      // Responsive positioning
      left: window.innerWidth < 768 ? 'auto' : '0',
      right: window.innerWidth < 768 ? '0' : 'auto',
      // For very small screens, position from right
      transform: window.innerWidth < 768 ? 'translateX(0)' : 'none',
      maxWidth: window.innerWidth < 400 ? 'calc(100vw - 20px)' : '280px',
    }}
  >
          {/* User Info Header - unchanged */}
          <div className="px-16 py-12 border-bottom border-gray-100">
            <div className="d-flex align-items-center gap-3">
              <div className="w-40 h-40 rounded-circle bg-main-600 text-white flex-center fw-bold fs-5">
                {getInitials()}
              </div>
              <div className="flex-grow-1">
                <div className="fw-medium text-dark">{user?.username}</div>
                <div className="text-xs text-gray-500">{user?.email}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              <span className={`badge px-8 py-2 rounded-4 ${user?.role === 'vendor' ? 'bg-warning-600' : 'bg-main-600'} text-white`}>
                {user?.role === 'vendor' ? 'Seller Account' : 'Customer Account'}
              </span>
            </div>
          </div>

          {/* Menu Items - unchanged */}
          <div className="py-8">
            <Link
              to="/profile"
              className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-user" /></span>
              <span>My Profile</span>
            </Link>

            <Link
              to="/orders"
              className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-package" /></span>
              <span>Orders</span>
            </Link>

            <Link
              to="/wishlist"
              className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-heart" /></span>
              <span>Wishlist</span>
            </Link>

            <Link
              to="/coupons"
              className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-ticket" /></span>
              <span>Coupons</span>
            </Link>

            {user?.role === 'vendor' && (
              <>
                <div className="border-top border-gray-100 my-8" />
                <Link
                  to="/vendor/dashboard"
                  className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
                  onClick={() => setIsOpen(false)}
                >
                  <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-storefront" /></span>
                  <span>Seller Dashboard</span>
                </Link>
                <Link
                  to="/vendor/products"
                  className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
                  onClick={() => setIsOpen(false)}
                >
                  <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-package" /></span>
                  <span>My Products</span>
                </Link>
              </>
            )}

            <Link
              to="/saved-addresses"
              className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-map-pin" /></span>
              <span>Saved Addresses</span>
            </Link>

            <Link
              to="/saved-cards"
              className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-credit-card" /></span>
              <span>Saved Cards & Wallet</span>
            </Link>

            <Link
              to="/notifications"
              className="d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-gray-700 transition-1 text-decoration-none"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-bell" /></span>
              <span>Notifications</span>
            </Link>
          </div>

          {/* Logout Button - unchanged */}
          <div className="border-top border-gray-100 pt-8">
            <button
              onClick={handleLogout}
              className="w-100 d-flex align-items-center gap-3 px-16 py-10 hover-bg-gray-100 text-danger-600 transition-1 border-0 bg-transparent cursor-pointer"
              style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
            >
              <span className="text-xl d-flex" style={{ width: '24px' }}><i className="ph ph-sign-out" /></span>
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;